import { Box, Typography, UserAvatar, UserFlag, Badge, PersonAddOutlinedIcon, Button, Fade, 
    PersonRemoveOutlinedIcon, AssistantOutlinedIcon, Stack } from 'src/components';
import { useIsMobile, useSnackbar } from 'src/hooks';
import { useState } from 'react';
import { toggleUserFriend, useAuth } from 'src/utils';
import { useFeatureTravellerStore } from 'src/stores';
import { useNavigate } from "react-router-dom";

export default function UserDetailsModalHeader() {
    const { user, setUser } = useAuth();
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { openSnackbar } = useSnackbar();

    const selectedTraveller = useFeatureTravellerStore((s) => s.selectedTraveller);
    const loadedTravellerData = useFeatureTravellerStore((s) => s.loadedTravellerData);

    const [isFollowing, setIsFollowing] = useState(user?.friends?.includes(selectedTraveller?.id));
    const [loadingFollowRequest, setLoadingFollowRequest] = useState(false);
    const [hovered, setHovered] = useState(null);

    if (!selectedTraveller && !loadedTravellerData[selectedTraveller?.id]) return null;
    const fullUser = loadedTravellerData[selectedTraveller?.id] || selectedTraveller;
    const { id, name, countryIsoAlpha2 } = fullUser;

    const handleMessageClick = () => {
        navigate(`/chat/direct/${id}`);
    };

    const toggleFollow = async () => {
        try {
            setLoadingFollowRequest(true);
            const res = await toggleUserFriend(id);
            setUser((prevUser) => ({...prevUser, friends: res}));
            setIsFollowing(res.includes(id));
            openSnackbar('Profile updated.', 'success');
        } catch (err) {
            console.error(err);
            openSnackbar('Failed to follow user, please try again.', 'error');
        } finally {
            setLoadingFollowRequest(false);
        }
    };

    const collapsedWidth = 32;
    const expandedWidth = 95;
    const buttonBaseSx = {
        borderRadius: 100,
        height: 32,
        overflow: "hidden",
        transition: "width 0.3s ease, padding 0.3s ease",
        justifyContent: "center",
        px: 1,
        minWidth: "auto",
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'nowrap',
                minWidth: 0
            }}
        >
            {/* Left side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexGrow: 1 }}>
                {countryIsoAlpha2 ? (
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={<UserFlag countryCode={countryIsoAlpha2} />}
                    >
                        <UserAvatar user={fullUser} size={48} />
                    </Badge>
                ) : (
                    <UserAvatar user={fullUser} size={48} />
                )}
                <Typography 
                    variant="h5" 
                    fontWeight="bold"
                    noWrap
                    sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        flexGrow: 1,
                        flexShrink: 1,
                        minWidth: 0
                    }}>
                        {name}
                    </Typography>
            </Box>

            {/* Right buttons */}
            {user.id !== id && (
                <Stack direction={isMobile ? "row" : "row"} spacing={1} alignItems="center" flexShrink={0}>
                    {/* Follow / Unfollow */}
                    <Button
                        variant="text"
                        onClick={toggleFollow}
                        startIcon={isFollowing ? <PersonRemoveOutlinedIcon /> : <PersonAddOutlinedIcon />}
                        sx={{
                            ...buttonBaseSx,
                            width: isMobile
                                ? collapsedWidth
                                : hovered === "follow"
                                ? expandedWidth
                                : collapsedWidth,
                        }}
                        onMouseEnter={() => !isMobile && setHovered("follow")}
                        onMouseLeave={() => !isMobile && setHovered(null)}
                        loading={loadingFollowRequest}
                    >
                        <Fade in={!isMobile && hovered === "follow"} timeout={200} unmountOnExit>
                            <Typography variant="body2" fontWeight="bold" noWrap>
                                {isFollowing ? 'Unfollow' : 'Follow'}
                            </Typography>
                        </Fade>
                    </Button>

                    {/* Message */}
                    <Button
                        variant="text"
                        onClick={handleMessageClick}
                        startIcon={<AssistantOutlinedIcon />}
                        sx={{
                            ...buttonBaseSx,
                            width: isMobile
                                ? collapsedWidth
                                : hovered === "message"
                                ? expandedWidth
                                : collapsedWidth,
                        }}
                        onMouseEnter={() => !isMobile && setHovered("message")}
                        onMouseLeave={() => !isMobile && setHovered(null)}
                    >
                        <Fade in={!isMobile && hovered === "message"} timeout={200} unmountOnExit>
                            <Typography variant="body2" fontWeight="bold" noWrap>
                                Message
                            </Typography>
                        </Fade>
                    </Button>
                </Stack>
            )}
        </Box>
    );
}
